import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, AlertTriangle } from 'lucide-react';
import { colors } from '../styles/colors';
import { updatePassword } from '../services/airtable';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/Nimbus_logo_only.png';
import logoName from '../assets/Nimbus_logo_name.png';

export default function ForcedPasswordChangePage() {
  const navigate = useNavigate();
  const { logout, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.newPassword.length < 8) {
      setError('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      // Utiliser le mot de passe temporaire stocké lors de la connexion
      const tempPassword = sessionStorage.getItem('temp_password');
      if (!tempPassword) {
        setError('Session expirée. Veuillez vous reconnecter.');
        logout();
        navigate('/login');
        return;
      }

      await updatePassword(tempPassword, formData.newPassword);

      // Nettoyer le mot de passe temporaire
      sessionStorage.removeItem('temp_password');

      // Mettre à jour le user dans le contexte pour retirer le flag forcePasswordChange
      updateUser({ forcePasswordChange: false });

      // Rediriger vers la page d'accueil
      navigate('/');
    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    paddingRight: '2.5rem',
    border: `1px solid ${colors.border.light}`,
    borderRadius: '6px',
    fontSize: '1rem',
    boxSizing: 'border-box',
    fontFamily: '"Noto Sans", sans-serif'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '500',
    color: colors.text.dark
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${colors.primary.teal} 0%, ${colors.primary.blue} 100%)`,
      padding: '2rem'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '500px',
        backgroundColor: 'white',
        borderRadius: '1rem',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '2rem',
          background: `linear-gradient(135deg, ${colors.primary.teal} 0%, ${colors.primary.blue} 100%)`,
          color: 'white',
          textAlign: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <img src={logo} alt="Logo" style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
            <img src={logoName} alt="Nimbus" style={{ height: '32px', objectFit: 'contain' }} />
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            marginTop: '1rem'
          }}>
            <AlertTriangle size={24} />
            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
              Changement de mot de passe requis
            </h1>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '2rem' }}>
          <div style={{
            backgroundColor: '#fef3c7',
            border: `2px solid #f59e0b`,
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            gap: '0.75rem'
          }}>
            <Lock size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: '0.125rem' }} />
            <p style={{ margin: 0, color: '#92400e', fontSize: '0.875rem', lineHeight: '1.5' }}>
              Pour des raisons de sécurité, vous devez changer votre mot de passe avant de continuer.
            </p>
          </div>

          {error && (
            <div style={{
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '6px',
              padding: '0.75rem',
              marginBottom: '1rem',
              color: '#c00',
              fontSize: '0.875rem'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Nouveau mot de passe */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>
                Nouveau mot de passe <span style={{ color: colors.primary.teal }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  style={inputStyle}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    color: colors.primary.gray
                  }}
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p style={{
                margin: '0.5rem 0 0 0',
                fontSize: '0.75rem',
                color: colors.text.light
              }}>
                Minimum 8 caractères
              </p>
            </div>

            {/* Confirmer le mot de passe */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>
                Confirmer le nouveau mot de passe <span style={{ color: colors.primary.teal }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  style={inputStyle}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    color: colors.primary.gray
                  }}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Boutons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              flexDirection: 'column'
            }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: loading ? colors.secondary.lightGray : colors.primary.teal,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: '"Noto Sans", sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <Lock size={18} />
                {loading ? 'Modification...' : 'Modifier le mot de passe'}
              </button>

              <button
                type="button"
                onClick={handleLogout}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'transparent',
                  color: colors.primary.gray,
                  border: `1px solid ${colors.border.light}`,
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: '"Noto Sans", sans-serif'
                }}
              >
                Se déconnecter
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
